import { SortOrder, type PaginationConfig, type RecordEntityId } from './../records/index';
import type { RecordEntity } from '../records';
import type { Attachments, Brand, GlobalMenuGroup, MenuLabel } from '../brand';
import type { Site } from '../site';
import type { Hours } from '../station';
import type { TranslationMap } from '../../../views/app-modules/brand-management/src/components';
import type { IntegrationPartner } from '../cd-config';

export interface V2Menu {
  date: {
    created: string;
    modified: string;
    published: string;
  };
  id: string;
  label: V2MenuLabel;
  groups: V2MenuGroup[];
  name?: string;
  parent_id?: string;
  location_brand?: string;
  company?: string;
  sector?: string;
  is?: Record<string, any>;
  meta?: Record<string, any>;
}

export interface V2MenuGroup {
  id?: string;
  name: string;
  label: V2MenuLabel;
  items: ModifierV2[];
}

export interface ModifierV2 {
  id: string;
  description?: V2MenuLabel;
  label: V2MenuLabel;
  nutrition?: any;
  certified?: any;
  meta?: MenuMeta;
  price?: {
    amount: number;
  };
  LTOPromo?: {
    price: { amount: number };
  };
  _index?: string;
  uniqueRadioId?: string;
  modifierGroupInfo?: V2MenuLabel;
}

export interface V2MenuHours {
  id: string;
  label: V2MenuLabel;
  hours: Hours[];
  is_pickup?: boolean;
  is_delivery?: boolean;
  is_frictionless?: boolean;
}

export interface V2MenuLabel {
  en: string;
  fr?: string;
}

export interface MenuMeta {
  brand: RecordEntityId;
  local_menu_group: RecordEntityId;
}

export interface LocalMenuGroupToSiteRelationship extends RecordEntity {
  site_id: RecordEntityId;
  local_menu_group_id: RecordEntityId;
  site_name: string;
  local_menu_group_name: string;
}
export interface LocalMenuGroup extends RecordEntity {
  name: string;
  id: string;
  is_active?: boolean;
  allowed_global_menu_groups?: Partial<GlobalMenuGroup[]>;
  draft_brands?: Brand[];
  published_brands?: Brand[];
  version?: number;
  vendor_metadata?: number;
  linked_sites?: LocalMenuGroupToSiteRelationship[];
  price_levels?: PriceLevelsMap;
  show_v4?: boolean;
  global_menu_groups_in_use?: RecordEntityId[];
  menu_labels?: {
    gmg_id: RecordEntityId;
    gmg_name: string;
    labels: Pick<MenuLabel, 'id' | 'text' | 's3_link'>[];
  }[];
  menuworks_unit_ids?: string[];
  visibility?: Visibility;
}

export interface PriceLevel {
  id: string;
  name: string;
  is_active: boolean;
  is_global?: boolean;
}

export interface PriceLevelsMap {
  [key: string]: PriceLevel;
}

export interface ItemModifierMenuLabelsMap {
  [key: string]: { is_active: boolean };
}

export interface AiImageGenerationOptions {
  aspect_ratio: AiImageAspectRatioOptions;
  background_color: AiImageBackgroundColorOptions;
  presentation?: AiImagePresentationOptions;
}

export enum AiImageAspectRatioOptions {
  LANDSCAPE = '16:9',
  SQUARE = '1:1',
}

export enum AiImageBackgroundColorOptions {
  WHITE = 'white',
  WARM_GREY = 'warm grey',
  BEIGE = 'beige',
}

export enum AiImagePresentationOptions {
  ITEM_ONLY = 'item only',
  PLATED = 'plated',
}

export enum MenuType {
  V2 = 'Admin Menu',
  CENTRIC_OS = 'Centric Menu',
}

export interface ModifierGroup extends RecordEntity {
  name: string;
  label?: string;
  min: number;
  max?: number;
  is_active?: boolean;
  parent_id?: RecordEntityId;
  brand_id?: RecordEntityId;
  items?: ItemToModifierGroupRelationship[];
  modifiers?: ModifierGroupToModifierRelationship[];
  priority?: number;
  version?: number;
  order_type: ModifierGroupOrderTypes;
  limit?: number;
  sizing?: ModifierGroupSizingOptions;
  duplicate_modifiers?: boolean;
  is_incremental?: boolean;
  parent?: Omit<ModifierGroup, 'parent'>;
  translation?: TranslationMap;
}

export interface Modifier extends RecordEntity {
  name: string;
  name_on_kds?: string;
  name_on_pos?: string;
  price: number;
  brand_id?: RecordEntityId;
  price_for_less?: number;
  price_for_more?: number;
  price_for_none?: number;
  pre_surcharge?: number;
  post_surcharge?: number;
  posid?: string;
  is_active: boolean;
  is_in_stock?: boolean;
  is_out_of_stock?: boolean;
  label?: string;
  calories?: number;
  description?: string;
  name_on_receipt?: string;
  tax_tags?: string[];
  tags?: string[];
  modifier_groups?: ModifierGroupToModifierRelationship[];
  modifier?: Modifier;
  priority?: number;
  weight?: Weight;
  reporting?: Reporting;
  version?: number;
  posid_segment?: string;
  parent_id?: RecordEntityId;
  attachments?: Attachments;
  serving_size?: number;
  menu_works?: MenuWorksEntity;
  tax_tag_code?: string;
  line_route?: ItemLineRoute;
  price_levels?: Record<string, number>;
  parent?: Omit<Modifier, 'parent'>;
  unique_id?: number;
  translation?: TranslationMap;
  menu_labels?: ItemModifierMenuLabelsMap;
}

export const enum ModifierGroupOrderTypes {
  SELECTION = 'selection',
  OPTION = 'option',
  QUANTITY = 'quantity',
}
export enum ModifierGroupSizingOptions {
  LESS_NORMAL_MORE = 'Less_Normal_More',
  NONE_NORMAL_EXTRA = 'None_Normal_Extra',
  SIDE_NORMAL_EXTRA = 'Side_Normal_Extra',
}

export interface ModifierBulk {
  id_list: RecordEntityId[];

  isActiveMultiple?: boolean;
  isNameMultiple?: boolean;
  isPriceMultiple?: boolean;
  isCaloriesMultiple?: boolean;
  isTagsMultiple?: boolean;
  isPriceForLessMultiple?: boolean;
  isPriceForMoreMultiple?: boolean;
  isPriceForNoneMultiple?: boolean;
  isPreSurchargeMultiple?: boolean;
  isPostSurchargeMultiple?: boolean;
  isLineRouteMultiple?: boolean;
  isTaxTagsMultiple?: boolean;
  isModifierGroupsMultiple?: boolean;
  isTaxTagCodeMultiple?: boolean;
  isMenuLabelsMultiple?: boolean;

  is_active?: boolean;
  name?: string;
  price?: number;
  price_for_less?: number;
  price_for_more?: number;
  price_for_none?: number;
  pre_surcharge?: number;
  post_surcharge?: number;
  reporting?: ReportingBulk;
  calories?: number;
  tax_tags?: string[];
  tags?: string[];
  modifier_groups?: ModifierGroupToModifierRelationship[];
  line_route?: ItemLineRoute;
  tax_tag_code?: string;
  menu_labels?: ItemModifierMenuLabelsMap;
}

export interface Weight {
  unit?: WeightUnit;
  amount?: number;
  isWeightAmountMultiple?: boolean;
  isWeightUnitMultiple?: boolean;
}

export interface Reporting {
  category?: ReportingCategory;
  jde_category?: string;
}

export interface ReportingCategory {
  primary?: ReportPrimaryCategory;
  secondary?: string;
}

export interface ReportingBulk {
  category?: ReportingCategoryBulk;
}

export interface ReportingCategoryBulk {
  primary?: ReportPrimaryCategory;
  secondary?: string;
  isReportingPrimaryMultiple?: boolean;
  isReportingSecondaryMultiple?: boolean;
}

export interface ReportingCategoryResponse extends RecordEntity {
  category?: string;
  occurrences?: number;
}

export enum JwoTaxCode {
  A_GEN_NOTAX = 'A_GEN_NOTAX',
  A_FOOD_GEN = 'A_FOOD_GEN',
  A_FOOD_CNDY = 'A_FOOD_CNDY',
  A_FOOD_CNDYFL = 'A_FOOD_CNDYFL',
  A_FOOD_BKTGN76_90 = 'A_FOOD_BKTGN76-90',
  A_FOOD_BKTGN50_75 = 'A_FOOD_BKTGN50-75',
  A_FOOD_JUICE0_50 = 'A_FOOD_JUICE0-50',
  A_FOOD_JUICE51_99 = 'A_FOOD_JUICE51-99',
  A_FOOD_CARBSFTDK = 'A_FOOD_CARBSFTDK',
  A_FOOD_CARBWTR = 'A_FOOD_CARBWTR',
  A_FOOD_NCARBWTR = 'A_FOOD_NCARBWTR',
  A_FOOD_WINE = 'A_FOOD_WINE',
}

export enum JwoTaxDescription {
  A_GEN_NOTAX = 'Always Nontaxable',
  A_FOOD_GEN = 'General Food',
  A_FOOD_CNDY = 'Candy',
  A_FOOD_CNDYFL = 'Candy Containing Flour',
  A_FOOD_BKTGN76_90 = 'General Food Basket 76-90%',
  A_FOOD_BKTGN50_75 = 'General Food Basket 50-75%',
  A_FOOD_JUICE0_50 = '0-50% Juice Content',
  A_FOOD_JUICE51_99 = '51-99% Juice Content',
  A_FOOD_CARBSFTDK = 'Carbonated Soft Drinks',
  A_FOOD_CARBWTR = 'Carbonated Water',
  A_FOOD_NCARBWTR = 'Non-Carbonated Water',
  A_FOOD_WINE = 'Wine',
}

export enum ReportPrimaryCategory {
  ALCOHOL = 'Alcohol',
  FOOD = 'Food',
  BEVERAGE = 'Beverage',
  GIFTCARDS = 'Gift Cards',
  SNACK = 'Snack',
  SUNDRY = 'Sundry',
}

export enum WeightUnit {
  LB = 'lb',
  OZ = 'oz',
  KG = 'kg',
  G = 'g',
}

export enum ItemLineRoute {
  GRILL = 'Grill',
  DELI = 'Deli',
  PIZZA = 'Pizza',
  PASTA = 'Pasta',
  SALAD = 'Salad',
  ENTREE = 'Entree',
  EXPO = 'Expo',
  COFFEE = 'Coffee',
  SUSHI = 'Sushi',
  BREAKFAST = 'Breakfast',
  CREATE = 'Create',
  EVERYTHING = 'Everything',
}

export const ALLOWED_ALLERGENS = [
  'milk',
  'egg',
  'fish',
  'shellfish - crustacean',
  'tree nuts',
  'peanuts',
  'wheat',
  'soy',
  'sesame',
  'mustard',
];

export interface LineRoute extends RecordEntity {
  line_route?: string;
  occurrences?: number;
}

export interface ModifierGroupToModifierRelationship extends RecordEntity {
  modifier_id: RecordEntityId;
  modifier_group_id: RecordEntityId;
  brand_id?: RecordEntityId;
  sequence?: number;
  modifier_group?: ModifierGroup;
  modifier?: Modifier;
  version?: number;
  parent_id?: string;
}

export interface Item extends RecordEntity {
  name: string;
  name_on_kds?: string;
  name_on_receipt?: string;
  name_on_pos?: string;
  price: number;
  reporting: Reporting;
  label?: string;
  description?: string;
  barcode?: string;
  calories?: number;
  is_published?: boolean;
  is_active?: boolean;
  is_in_stock?: boolean;
  is_out_of_stock?: boolean;
  posid?: string;
  tax_tag_code?: string;
  tax_tags?: string[];
  tags?: string[];
  modifier_groups?: ItemToModifierGroupRelationship[];
  categories?: CategoryToItemRelationship[];
  priority?: number;
  weight?: Weight;
  line_route?: ItemLineRoute;
  attachments?: Attachments;
  version?: number;
  posid_segment?: string;
  parent_id?: RecordEntityId;
  serving_size?: number;
  menu_works?: MenuWorksEntity;
  is_featured?: boolean;
  meal_value?: string;
  tax_jwo_code?: JwoTaxCode;
  brand_id?: RecordEntityId;
  base_item_id?: string;
  price_levels?: Record<string, number>;
  parent?: Omit<Item, 'parent'>;
  unique_id?: number;
  translation?: TranslationMap;
  menu_labels?: ItemModifierMenuLabelsMap;
}

export enum ActivityStatusType {
  PUBLISH = 'publish',
  IMPORT = 'import',
  UPDATE_NEXTEP = 'update_nextep',
  UPDATE_TIME2EAT = 'update_time2eat',
  UPDATE_VOLANTE = 'update_volante',
  UPDATE_AGILYSYS = 'update_agilysys',
  UPDATE_AGILYSYS_DOD = 'update_agilysys_dod',
  UPDATE_MASHGIN = 'update_mashgin',
  UPDATE_TRANSACT = 'update_transact',
  UPDATE_STARSHIP = 'update_starship',
  UPDATE_GRUBHUB = 'update_grubhub',
  MENU_TO_V2_MENU = 'menu_to_v2_menu',
  POSID_GENERATION = 'posid_generation',
  FETCH_GLOBAL_UPDATES = 'fetch_global_updates',
  GLOBAL_MENU_GROUP_CLEANUP = 'global_menu_group_cleanup',
  LOCAL_MENU_GROUP_CLEANUP = 'local_menu_group_cleanup',
  BRAND_CLEANUP = 'brand_cleanup',
  UNPUBLISHED_CHANGES_APPLIED = 'unpublished_changes_applied',
  GENERATE_DIFF = 'generate_diff',
  APPLY_DIFF = 'apply_diff',
  UPDATE_JWO = 'update_jwo',
  V2_MENU_DELETED = 'V2MenuDeleted',
  NEXTEP_SYNC_RESULT = 'nextep_sync_result',
  VOLANTE_SYNC_RESULT = 'volante_sync_result',
  AGILYSYS_SYNC_RESULT = 'agilysys_sync_result',
  AGILYSYS_DOD_SYNC_RESULT = 'agilysys_dod_sync_result',
  TIME2EAT_SYNC_RESULT = 'time2eat_sync_result',
  MASHGIN_SYNC_RESULT = 'mashgin_sync_result',
  IMPORT_BRAND_EXCEL = 'import_brand_excel',
  MENU_HYDRATION = 'menu_hydration',
  BRAND_V3_TO_V4 = 'brand_v3_to_v4',
  MENU_SCHEDULE_MIGRATION = 'menu_schedule_migration',
  UPDATE_INTEGRATED_PARTNERS = 'update_integrated_partners',
  MENUWORKS_SYNC = 'menuworks_sync',
  MENU_CREATE = 'menu_create',
  MENU_UPDATE = 'menu_update',
  MENU_DELETE = 'menu_delete',
  MENU_SCHEDULE = 'menu_schedule',
  MENU_UNSCHEDULE = 'menu_unschedule',
  CATEGORY_CREATE = 'category_create',
  CATEGORY_UPDATE = 'category_update',
  CATEGORY_DELETE = 'category_delete',
  ITEM_CREATE = 'item_create',
  ITEM_UPDATE = 'item_update',
  ITEM_DELETE = 'item_delete',
  MODIFIER_GROUP_CREATE = 'modifier_group_create',
  MODIFIER_GROUP_UPDATE = 'modifier_group_update',
  MODIFIER_GROUP_DELETE = 'modifier_group_delete',
  MODIFIER_CREATE = 'modifier_create',
  MODIFIER_UPDATE = 'modifier_update',
  MODIFIER_DELETE = 'modifier_delete',
  MENU_LABEL_CREATE = 'menu_label_create',
  MENU_LABEL_IMAGE_UPDATE = 'menu_label_image_update',
  MENU_LABEL_REMOVE = 'menu_label_remove',
  GMG_ALLERGEN_VISIBILITY_UPDATE = 'gmg_allergen_visibility_update',
  LOCAL_MENU_GROUP_UPDATE = 'local_menu_group_update',
}

export enum ActivityStatus {
  FAILED = 'failed',
  INITIATED = 'initiated',
  SUCCESSFUL = 'successful',
  IN_PROGRESS = 'in_progress',
}

export enum ActivityDataVariableType {
  BRAND = 'brand',
  CATEGORY = 'category',
  ENTITY = 'entity',
  GLOBAL_MENU_GROUP = 'global_menu_group',
  LOCAL_MENU_GROUP = 'local_menu_group',
  ITEM = 'item',
  MENU = 'menu',
  MENU_LABEL = 'menu_label',
  MODIFIER = 'modifier',
  MODIFIER_GROUP = 'modifier_group',
  NUMBER = 'number',
  PARTNER = 'partner',
  PRICE_LEVEL = 'price_level',
  STATION = 'station',
  USER = 'user',
}

export interface ActivityDataVariable {
  type: ActivityDataVariableType;
  label: string;
  id?: RecordEntityId;
}

export interface ActivityDataDeleted {
  at: string;
  by: string;
}

export interface ActivityDataFailuresDetails {
  error?: string;
  id?: RecordEntityId;
  name?: string;
}
export interface ActivityDataFailures {
  item?: ActivityDataFailuresDetails[];
  modifier?: ActivityDataFailuresDetails[];
}

export interface ActivityDataAffectedEntity {
  id: RecordEntityId;
  name: string;
}

export interface ActivityData {
  template?: string;
  variables?: Record<string, ActivityDataVariable>;
  global_audit_trail?: RecordEntityId[];
  deleted?: ActivityDataDeleted;
  brand_name?: string;
  initiator_name?: string;
  partner?: IntegrationPartner;
  items_synced?: number;
  modifiers_synced?: number;
  failures?: ActivityDataFailures;
  affected_items?: ActivityDataAffectedEntity[];
  affected_modifiers?: ActivityDataAffectedEntity[];
}

export interface ActivityUser {
  name: string;
  email?: string;
}

export interface Activity extends Omit<RecordEntity, 'status'> {
  process_id: RecordEntityId;
  version?: number;
  brand?: Brand;
  brand_id: RecordEntityId;
  status_type: ActivityStatusType;
  status: ActivityStatus;
  message: string;
  data?: ActivityData;
  s3_link?: string;
  email?: string;
}

// Pulled JWOItem && JWOProcessingResponse from Menu BE
export interface JWOItem {
  item_sku: string;
  external_product_id: string;
  item_name: string;
  store_id: string;
  standard_price: string;
  product_category: string;
  product_subcategory: string;
  brand_name: string;
  product_tax_code: string;
}

export interface JWOProcessingResponse {
  catalogProcessingSummary: {
    totalRecords: number;
    errorRecords: number;
    successRecords: number;
    warningRecords: number;
  };
  catalogProcessingResults: {
    submittedCatalog: JWOItem;
    processingResult: {
      status: string;
      message: string;
    };
  }[];
}

export interface Allergen {
  name: string;
  contains: string;
}
export interface NutritionValue {
  amount: number;
  unit: string;
}

/**
 * Single source of truth for nutrition fact keys.
 * Used by Nutrition (actual values), NutritionVisibility (display toggles), and UI components.
 */
export const NUTRITION_KEYS = [
  'calories',
  'total_fat',
  'saturated_fat',
  'trans_fat',
  'total_carbohydrate',
  'total_sugars',
  'added_sugars',
  'cholesterol',
  'protein',
  'dietary_fiber',
  'sodium',
] as const;

export type NutritionKey = (typeof NUTRITION_KEYS)[number];

// Nutrition fact values (amount + unit) - used when displaying actual nutrition data
export type Nutrition = Partial<Record<NutritionKey, NutritionValue>>;

// Visibility toggles for nutrition facts - controls which nutrition facts are shown in guest menus
export type NutritionVisibility = Partial<Record<NutritionKey, boolean>>;

export interface Visibility {
  allergens?: boolean;
  menu_labels?: boolean;
  nutrition?: NutritionVisibility;
}

// MenuWorks nutrition / smart-tag style icon
export interface MenuWorksIcon {
  id: number;
  name?: string;
  url: string;
  rank?: number;
}

export interface MenuWorksResponse {
  calories?: number;
  name?: string;
  short_name?: string;
  description?: string;
  MRN?: string;
  unit_id?: string;
  portion_quantity?: number;
  portion_unit_name?: string;
  grams?: number;
  cost?: number;
  ingredients?: string[];
  smart_tags?: string[];
  allergen_tags?: string[];
  allergens?: Allergen[];
  nutrition?: Nutrition;
  icons?: MenuWorksIcon[];
  updated_at?: string | Date;
}

export interface MenuWorksEntity {
  MRN: string;
  unit_id?: string;
  portion_quantity: number;
  portion_unit_name: string;
  grams?: number;
  cost: number;
  ingredients: string[];
  smart_tags: string[];
  allergen_tags: string[];
  allergens: Allergen[];
  nutrition?: Nutrition;
  icons?: MenuWorksIcon[];
  updated_at?: string | Date;
}

export interface MenuWorksBusinessUnit {
  id: string;
  name?: string;
  sector_name?: string;
}

/**
 * MenuWorks product from the postgres cache API.
 * Uses `mrn` (MenuWorks Recipe Number) as the unique identifier.
 */
export interface MenuWorksProduct {
  mrn: string;
  product_type: string;
  name?: string;
  short_name?: string;
}

/**
 * Sync status values for products sync.
 */
export enum ProductsSyncStatus {
  IDLE = 'idle',
  INITIATED = 'initiated',
  SYNCING = 'syncing',
  FAILED = 'failed',
}

/**
 * Sync status object included in the products list response.
 * Used for the sync_status field from GET .../products.
 */
export interface ProductsSyncStatusResponseDTO {
  status: ProductsSyncStatus;
  items_ingested_count: number;
  total_available?: number;
  last_success_at?: string;
  started_at?: string;
}

export enum ItemImportErrorFields {
  NAME = 'name',
  PRICE = 'price',
  LABEL = 'label',
  REPORTING = 'reporting',
  DESCRIPTION = 'description',
  BARCODE = 'barcode',
  CALORIES = 'calories',
  IS_ACTIVE = 'is_active',
  TAX_TAGS = 'tax_tags',
  TAGS = 'tags',
  WEIGHT_UNIT = 'weight',
  WEIGHT_AMOUNT = 'weight_amount',
  LINE_ROUTE = 'line_route',
  CATEGORY_ID = 'category_id',
  ID = 'id',
  JWO_TAX_CODE = 'tax_jwo_code',
  UNKNOWN = 'unknown',
}

export interface ItemBulk {
  id_list: RecordEntityId[];
  isNameMultiple?: boolean;
  isPriceMultiple?: boolean;
  isMealValueMultiple?: boolean;
  isLabelMultiple?: boolean;
  isDescriptionMultiple?: boolean;
  isCaloriesMultiple?: boolean;
  isTaxTagsMultiple?: boolean;
  isTagsMultiple?: boolean;
  isModifierGroupsMultiple?: boolean;
  isLineRouteMultiple?: boolean;
  isActiveMultiple: boolean;
  isFeaturedMultiple?: boolean;
  isTaxTagCodeMultiple?: boolean;
  isJwoTaxCodeMultiple?: boolean;
  isMenuLabelsMultiple?: boolean;

  is_active?: boolean;
  name?: string;
  price?: number;
  meal_value?: number;
  reporting?: ReportingBulk;
  label?: string;
  description?: string;
  calories?: number;
  tax_tags?: string[];
  tags?: string[];
  modifier_groups?: ItemToModifierGroupRelationship[];
  line_route?: ItemLineRoute;
  is_featured?: boolean;
  tax_jwo_code?: JwoTaxCode;
  tax_tag_code?: string;
  menu_labels?: ItemModifierMenuLabelsMap;
}

export interface ItemToModifierGroupRelationship extends RecordEntity {
  item_id: RecordEntityId;
  modifier_group_id: RecordEntityId;
  modifier_group?: ModifierGroup;
  brand_id?: RecordEntityId;
  name?: string;
  item?: Item;
  sequence?: number;
  version?: number;
  parent_id?: RecordEntityId;
}

export interface Category extends RecordEntity {
  name: string;
  label?: string;
  sequence?: number;
  priority?: number;
  is_active?: boolean;
  items?: CategoryToItemRelationship[];
  version?: number;
  attachments?: Attachments;
  menu_id?: RecordEntityId;
  brand_id?: RecordEntityId;
  created_at: string;
  parent_id?: RecordEntityId;
  parent?: Omit<Category, 'parent'>;
  translation?: TranslationMap;
}

export interface CategoryToItemRelationship extends RecordEntity {
  category_id?: RecordEntityId;
  item_id?: RecordEntityId;
  brand_id?: RecordEntityId;
  parent_id?: RecordEntityId;
  sequence?: number;
  category?: Category;
  item?: Item;
  version?: number;
  created_at?: string;
  deleted_at?: string;
  updated_at?: string;
}

export interface Menu extends RecordEntity {
  id: RecordEntityId;
  parent_id?: RecordEntityId;
  station_id?: RecordEntityId;
  name: string;
  brand?: Brand;
  brand_id?: RecordEntityId;
  created_at?: string;
  station?: Station;
  categories?: Category[];
  version?: number;
  price_level_id?: RecordEntityId;
  parent?: Omit<Menu, 'parent'>;
  translation?: TranslationMap;
  children?: Menu[];
  attachments?: Attachments;
}

export interface MenuSizeDTO {
  format: string;
  raw: number;
  compressed: number;
}

export interface MenuSizeResponse {
  id: string;
  name: string;
  size: MenuSizeDTO;
}

/** 6MB Lambda limit for menu payload size */
export const MENU_SIZE_LIMIT_BYTES = 6 * 1024 * 1024;
/** 4MB (80% of limit) - warning threshold */
export const MENU_SIZE_WARN_THRESHOLD = 4 * 1024 * 1024;

export interface MenuWithTypeAndCatCount extends Menu {
  totalCategories?: number;
  menuSize?: number; // Size in bytes (raw size from MenuSizeDTO)
  meta?: {
    menu_type?: MenuType;
  };
}

export interface Station extends RecordEntity {
  name: string;
  site?: Site;
}

export const defaultMenuNavigationPaginationConfig: PaginationConfig = {
  queryParams: {
    limit: 25,
    page: 1,
    sortBy: 'name',
    sortOrder: SortOrder.ASC,
    name: null,
    station: null,
  },
  metaParams: {
    resultCount: 0,
    totalPages: 1,
    totalResults: 0,
  },
};

export const defaultMenuRecordPaginationConfig: PaginationConfig = {
  queryParams: {
    limit: 25,
    page: 1,
    sortBy: 'name',
    sortOrder: SortOrder.ASC,
    name: null,
    price: null,
    posid: null,
    barcode: null,
    is_active: null,
  },
  metaParams: {
    resultCount: 0,
    totalPages: 1,
    totalResults: 0,
  },
};

export const defaultCategoryToItemPaginationConfig: PaginationConfig = {
  queryParams: {
    limit: 25,
    page: 1,
    sortBy: 'sequence',
    sortOrder: SortOrder.ASC,
    name: null,
    price: null,
    posid: null,
    barcode: null,
    is_active: null,
  },
  metaParams: {
    resultCount: 0,
    totalPages: 1,
    totalResults: 0,
  },
};

export const defaultModifierGroupToModifierPaginationConfig: PaginationConfig = {
  queryParams: {
    limit: 25,
    page: 1,
    sortBy: 'sequence',
    sortOrder: SortOrder.ASC,
  },
  metaParams: {
    resultCount: 0,
    totalPages: 1,
    totalResults: 0,
  },
};

export interface StockEntity {
  id: RecordEntityId;
  business_unit_id: RecordEntityId;
  item_id: RecordEntityId;
  menu_id: RecordEntityId;
  brand_id: RecordEntityId;
  item_name: string;
  brand_name: string;
  is_in_stock: boolean;
  draft_is_in_stock: boolean;
  version: number;
  created_at: string;
  updated_at: string;
  deleted_at?: string;
}

export interface PatchStockPayload {
  business_unit_id: RecordEntityId;
  item_ids: RecordEntityId[];
  draft_is_in_stock: boolean;
}

export const defaultStockRecordPaginationConfig: PaginationConfig = {
  queryParams: {
    limit: 25,
    page: 1,
    sortBy: 'item_name',
    sortOrder: SortOrder.ASC,
    item_name: null,
    brand_id: null,
    draft_is_in_stock: null,
    type: null,
  },
  metaParams: {
    resultCount: 0,
    totalPages: 1,
    totalResults: 0,
  },
};

export const defaultActivityRecordPaginationConfig: PaginationConfig = {
  queryParams: {
    limit: 25,
    page: 1,
    sortBy: 'created_at',
    sortOrder: SortOrder.DESC,
    date: null,
    message: null,
    processId: null,
  },
  metaParams: {
    resultCount: 0,
    totalPages: 1,
    totalResults: 0,
  },
};

export const defaultMenusNavigationPaginationConfig: PaginationConfig = {
  queryParams: {
    limit: 25,
    page: 1,
    sortBy: 'name',
    sortOrder: SortOrder.ASC,
  },
  metaParams: {
    resultCount: 0,
    totalPages: 1,
    totalResults: 0,
  },
};

export const defaultCpgItemsPaginationConfig: PaginationConfig = {
  queryParams: {
    limit: 25,
    page: 1,
    sortBy: 'name',
    sortOrder: SortOrder.ASC,
    name: null,
    barcode: null,
  },
  metaParams: {
    resultCount: 0,
    totalPages: 1,
    totalResults: 0,
  },
};

type QueryParamValueType = string | number | Object;

export interface MenuQueryParams extends Record<string, QueryParamValueType> {
  limit?: number;
  page?: number;
  sortBy?: string;
  sortOrder?: string;
  name?: string;
  price?: number;
  posid?: string;
  barcode?: string;
  is_active?: boolean;
  station?: Object;
  filter?: Object;
}

export interface StockQueryParams extends Record<string, QueryParamValueType> {
  limit?: number;
  page?: number;
  sortBy?: string;
  sortOrder?: string;
  item_name?: string;
  brand_id?: RecordEntityId;
  draft_is_in_stock?: boolean;
  filter?: Object;
}

export interface ActivityQueryParams extends Record<string, QueryParamValueType> {
  limit?: number;
  page?: number;
  sortBy?: string;
  sortOrder?: string;
  created_at?: Object;
  message?: string;
  status_type?: string;
  processId?: RecordEntityId;
  status?: string;
  email?: string;
  filter?: Object;
}

export interface FilterQueryParams extends Record<string, QueryParamValueType> {
  name?: Object;
  price?: Object;
  posid?: Object;
  barcode?: Object;
  is_active?: Object;
  station?: { name: Object };
}
export interface V3Menu extends V2Menu {
  brand?: string;
}

export enum TaxTags {
  Alcohol = 'Alcohol',
  BakedGoods = 'Baked Goods',
  Candy = 'Candy',
  CarbonatedBeverage = 'Carbonated Beverage',
  TaxExempt = 'Tax Exempt',
  Pop = 'Pop',
  Prepared = 'Prepared',
  SweetenedBeverage = 'Sweetened Beverage',
  Unprepared = 'Unprepared',
  Water = 'Water',
  Meal = 'Meal',
  Good = 'Good',
}

export interface UniversalCPGItem extends RecordEntity {
  barcode: string;
  name: string;
  description?: string;
  created_at?: string | number | Date;
  updated_at?: string | number | Date;
  is_verified?: boolean;
  verified_at?: Date;
  verified_by?: string;
  images?: Record<string, any>;
  metadata: Record<string, any>;
  product_number?: number;
}

export interface MenuPublishError {
  barcode: string;
  items: Item[];
  type: string;
}

export interface GroupExportSolution<T = string | string[]> {
  id: string;
  name: string;
  format: T;
}

export enum BrandSubEntities {
  MENU = 'menu',
  CATEGORY = 'category',
  ITEM = 'item',
  MODIFIER_GROUP = 'modifier-group',
  MODIFIER = 'modifier',
}

export interface ExportBrandResponse {
  s3_link: string;
}
export interface ImportLinkBrandResponse {
  upload_link: string;
}
export interface ImportBrandResponse {
  success: boolean;
  errors_report_link?: string;
}

export interface CatalogBackupVersion {
  version_id: string;
  last_modified: string;
}

export type EntityToEntityRelationship =
  | ModifierGroupToModifierRelationship
  | ItemToModifierGroupRelationship
  | CategoryToItemRelationship;
